import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {Map<string, Document>} */
  static #documentCache = new Map();

  /** @type {Map<string, Element>} */
  static #productGridCache = new Map();

  /** @type {Map<string, Promise<Document | null>>} */
  static #pendingFetches = new Map();

  static #maxCacheEntries = 16;

  /** @type {boolean} */
  #isOpening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();

    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card.
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));

    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#isOpening = false;
  }

  /**
   * @param {string} url
   * @param {Document} html
   */
  static #cacheDocument(url, html) {
    if (!url || !html) return;

    if (!QuickAddComponent.#documentCache.has(url)) {
      QuickAddComponent.#documentCache.set(url, html);
    }

    const gridElement = html.querySelector('[data-product-grid-content]');

    if (gridElement && !QuickAddComponent.#productGridCache.has(url)) {
      QuickAddComponent.#productGridCache.set(url, /** @type {Element} */ (gridElement.cloneNode(true)));
    }

    QuickAddComponent.#trimCache();
  }

  static #trimCache() {
    while (QuickAddComponent.#documentCache.size > QuickAddComponent.#maxCacheEntries) {
      const firstKey = QuickAddComponent.#documentCache.keys().next().value;

      if (!firstKey) break;

      QuickAddComponent.#documentCache.delete(firstKey);
      QuickAddComponent.#productGridCache.delete(firstKey);
    }
  }

  /**
   * @param {string} url
   * @returns {Element | null}
   */
  static #getCachedProductGrid(url) {
    const cachedGrid = QuickAddComponent.#productGridCache.get(url);

    if (!cachedGrid) return null;

    return /** @type {Element} */ (cachedGrid.cloneNode(true));
  }

  /**
   * Handles quick add button click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#isOpening) return;

    this.#isOpening = true;

    try {
      const currentUrl = this.productPageUrl;

      let productGrid = QuickAddComponent.#getCachedProductGrid(currentUrl);

      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        const gridElement = html?.querySelector('[data-product-grid-content]');

        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        }
      }

      if (!productGrid) return;

      // Register the gallery boundaries before morphing their dialog refs into the modal.
      await Promise.all([import('@theme/media-gallery'), import('./zoom-dialog.js')]);

      await this.updateQuickAddModal(productGrid);
      this.#openQuickAddModal();
    } finally {
      this.#isOpening = false;
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');

    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  /**
   * Fetches and caches the product page content.
   * ProductCard prefetch also calls this method, so the cache is shared across cards.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cachedDocument = QuickAddComponent.#documentCache.get(productPageUrl);

    if (cachedDocument) {
      return cachedDocument;
    }

    const pendingFetch = QuickAddComponent.#pendingFetches.get(productPageUrl);

    if (pendingFetch) {
      return pendingFetch;
    }

    const fetchPromise = fetch(productPageUrl)
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        QuickAddComponent.#cacheDocument(productPageUrl, html);

        return html;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        QuickAddComponent.#pendingFetches.delete(productPageUrl);
      });

    QuickAddComponent.#pendingFetches.set(productPageUrl, fetchPromise);

    return fetchPromise;
  }

  /**
   * Re-renders the quick add modal.
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      if (!productDetails || !productFormComponent || !variantPicker || !productPrice) return;

      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);

      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal.
   * @param {Element} modalContent
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #needsIOSGridPatch = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const iosVersion = getIOSVersion();

    this.#needsIOSGridPatch = Boolean(
      iosVersion && (iosVersion.major < 16 || (iosVersion.major === 16 && iosVersion.minor < 4))
    );

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#abortController.signal,
    });

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Closes the dialog.
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    this.closeDialog();
  };

  #handleBreakpointChange = () => {
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement | null} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );

    if (!anchorElement) return;

    const viewMoreDetailsLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.product-header a'));

    if (viewMoreDetailsLink && viewMoreDetailsLink.href !== anchorElement.href) {
      viewMoreDetailsLink.href = anchorElement.href;
    }

    if (mobileProductTitle && mobileProductTitle.href !== anchorElement.href) {
      mobileProductTitle.href = anchorElement.href;
    }
  };

  #handleDialogClose = () => {
    if (!this.#needsIOSGridPatch) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');

      if (!grid) return;

      const currentWidth = grid.getBoundingClientRect().width;

      grid.style.width = `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}